import React from 'react';
import TextBox from 'components/TextBox';
import i18n from 'constants/translation/i18n';
import CustomIconButton from 'components/CustomIconButton';
import CustomSelect from 'components/CustomSelect';
import TextAreaBoxSolutions from 'components/TextAreaBoxSolutions';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import {
  IGapsInfo,
  IAudits,
  IAuditFindingRow,
  IAdditionalInfo
} from 'store/ducks/Gaps/types';
import { solutionsOp } from 'store/ducks/Solutions';
import { YesNo } from 'store/ducks/globalTypes';
import Helpers from 'utils/Helpers';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { ISolutionTableRow } from 'store/ducks/Solutions/types';
import * as Styled from 'pages/SolutionDefinition/SolutionDefinition.styled';
import * as StyledAudits from 'components/Tables/AuditFindingsTable/AuditFindingsTable.styled';
import * as StyledAdditional from 'components/Tables/AdditionalInfoTable/AdditionalInfoTable.styled';
import AuditsSelectBox from 'components/AuditsSelectBox';
import CustomDatePicker from 'components/CustomDatePicker';
import { stripHtmlTags } from 'utils/stripHtmlTags';

interface AuditFindingColumnsProps {
  scheme: string;
  token: any;
  fetchDeleteAudit: (audit: IAudits) => void;
}

const AuditFindingColumns = ({
  scheme,
  token,
  fetchDeleteAudit
}: AuditFindingColumnsProps) => [
  {
    title: i18n.t('CLN_REQ'),
    dataIndex: 'value',
    width: 300,
    render: (value: string) => (
      <StyledAudits.ClientReqTextArea
        value={stripHtmlTags(value)}
        rows={3}
        level={0}
        width={300}
        readOnly={false}
        scheme={scheme}
      />
    )
  },
  {
    title: i18n.t('CATEGORY'),
    dataIndex: 'category',
    width: 100,
    render: (category: string) => (
      <CustomSelect options={[]} defaultValue={category} disabled />
    )
  },
  {
    title: i18n.t('ALIGNED_AUDIT_FINDINGS'),
    dataIndex: 'audits',
    width: 400,
    render: (audits: IAudits[]) =>
      audits?.length ? (
        <div>
          {audits.map((audit) => (
            <TextBox key={audit.afg_id} width={400}>
              {stripHtmlTags(audit.afg_desc)}
            </TextBox>
          ))}
        </div>
      ) : (
        <Styled.StyledTextBox width={400} token={token} />
      )
  },
  {
    title: 'Actions',
    width: 60,
    align: 'center',
    render: (_: any, row: IAuditFindingRow) =>
      row.audits?.length
        ? row.audits.map((audit) => (
            <StyledAudits.StyledButton
              key={audit.afg_id}
              type='primary'
              token={token}
              icon={<DeleteOutlined />}
              onClick={() => fetchDeleteAudit(audit)}
            />
          ))
        : null
  }
];

const SolutionColumns = ({
  solutionTypes,
  approversList,
  permitted,
  dispatch,
  handleStatusChange,
  handlePlusClick,
  handleDelete,
  handleDeleteLocal,
  clnReqSearch,
  clnReqSearchOpen,
  setClnReqSearch,
  setClnReqSearchOpen,
  gapsSearch,
  gapsSearchOpen,
  setGapsSearch,
  setGapsSearchOpen,
  solSearch,
  solSearchOpen,
  setSolSearch,
  setSolSearchOpen
}: {
  solutionTypes: string[];
  approversList: string[];
  permitted: boolean;
  dispatch: any;
  handleStatusChange: (value: YesNo, solId: string | number) => void;
  handlePlusClick: (row: ISolutionTableRow) => void;
  handleDelete: (id: number) => void;
  handleDeleteLocal: (id: string | number) => void;
  clnReqSearch: string;
  clnReqSearchOpen: boolean;
  setClnReqSearch: (v: string) => void;
  setClnReqSearchOpen: (v: boolean) => void;
  gapsSearch: string;
  gapsSearchOpen: boolean;
  setGapsSearch: (v: string) => void;
  setGapsSearchOpen: (v: boolean) => void;
  solSearch: string;
  solSearchOpen: boolean;
  setSolSearch: (v: string) => void;
  setSolSearchOpen: (v: boolean) => void;
}) => {
  const solutionColumns = [
    {
      title: i18n.t('CATEGORY'),
      width: 100,
      render: (_: any, row: ISolutionTableRow) => ({
        children: (
          <CustomSelect options={[]} defaultValue={row.category} disabled />
        ),
        props: {
          rowSpan: row.bruRowSpan
        }
      })
    },

    {
      title: () => (
        <Styled.ColumnHeaderContainer>
          <span>{i18n.t('CLN_REQ')}</span>
          <Styled.SearchIcon
            onClick={() => setClnReqSearchOpen(!clnReqSearchOpen)}
          />
          {clnReqSearchOpen && (
            <Styled.StyledSearchInput
              placeholder='Search'
              value={clnReqSearch}
              onChange={(e) => setClnReqSearch(e.target.value)}
              autoFocus
            />
          )}
        </Styled.ColumnHeaderContainer>
      ),
      width: clnReqSearchOpen ? 700 : 500,
      render: (_: unknown, row: ISolutionTableRow) => ({
        children: (
          <TextBox width={clnReqSearchOpen ? 700 : 500} level={row.level}>
            {stripHtmlTags(row.value)}
          </TextBox>
        ),
        props: {
          rowSpan: row.bruRowSpan
        }
      })
    },

    {
      title: () => (
        <Styled.ColumnHeaderContainer>
          <span>{i18n.t('GAPS')}</span>
          <Styled.SearchIcon
            onClick={() => setGapsSearchOpen(!gapsSearchOpen)}
          />
          {gapsSearchOpen && (
            <Styled.StyledSearchInput
              placeholder='Search'
              value={gapsSearch}
              onChange={(e) => setGapsSearch(e.target.value)}
              autoFocus
            />
          )}
        </Styled.ColumnHeaderContainer>
      ),
      width: 335,
      render: (_: unknown, row: ISolutionTableRow) =>
        !Helpers.isObjEmpty(row.GAPS) ? (
          <Styled.TextBoxContainer>
            {Object.values(row.GAPS)
              .filter((gap: IGapsInfo) =>
                gapsSearch.trim()
                  ? gap.gap_desc
                      ?.toLowerCase()
                      .includes(gapsSearch.toLowerCase())
                  : true
              )
              .map((gap: IGapsInfo) => (
                <TextBox key={gap.gap_id} width={335}>
                  {stripHtmlTags(gap.gap_desc)}
                </TextBox>
              ))}
          </Styled.TextBoxContainer>
        ) : (
          <Styled.StyledTextBox width={335} />
        )
    },

    {
      title: i18n.t('AUDIT_FINDINGS'),
      width: 335,
      render: (_: any, row: ISolutionTableRow) =>
        !Helpers.isObjEmpty(row.AUDITS) ? (
          <Styled.TextBoxContainer>
            {Object.values(row.AUDITS).map((audit: IAudits) => (
              <TextBox key={generateUniqueId()} width={335}>
                {stripHtmlTags(audit.desc)}
              </TextBox>
            ))}
          </Styled.TextBoxContainer>
        ) : (
          <Styled.StyledTextBox width={335} />
        )
    },

    {
      title: () => (
        <Styled.ColumnHeaderContainer>
          <span>{i18n.t('SOLUTIONS')}</span>
          <Styled.SearchIcon onClick={() => setSolSearchOpen(!solSearchOpen)} />
          {solSearchOpen && (
            <Styled.StyledSearchInput
              placeholder='Search'
              value={solSearch}
              onChange={(e) => setSolSearch(e.target.value)}
              autoFocus
            />
          )}
        </Styled.ColumnHeaderContainer>
      ),
      width: 376,
      render: (_: unknown, row: ISolutionTableRow) => (
        <>
          {!Helpers.isObjEmpty(row.SOLUTIONS) && (
            <TextAreaBoxSolutions
              solutions={row.SOLUTIONS}
              solId={row.sol_id}
              onDelete={handleDeleteLocal}
            />
          )}

          {row.isLastSolutionRow && (
            <Styled.ButtonContainer>
              <CustomIconButton
                type='primary'
                shape='circle'
                icon={<PlusOutlined />}
                size='middle'
                onClick={() => handlePlusClick(row)}
              />
            </Styled.ButtonContainer>
          )}
        </>
      )
    },

    {
      title: i18n.t('SOLUTION_TYPE'),
      width: 100,
      render: (_: any, row: ISolutionTableRow) => {
        const options = Helpers.convertToSelectOptions(solutionTypes);

        return (
          <CustomSelect
            options={options}
            defaultValue={row.SOLUTIONS?.sol_type}
            onChange={(value: string) => {
              dispatch(
                solutionsOp.editSolutionValue(
                  row.sol_id,
                  row.SOLUTIONS,
                  'sol_type',
                  value
                )
              );

              dispatch(complianceEstOp.setSaveDisabled(false));
            }}
          />
        );
      }
    },

    {
      title: i18n.t('EVIDENCE_REQUIRED'),
      width: 150,
      render: (_: any, row: ISolutionTableRow) => (
        <CustomSelect
          size='lg'
          options={[
            {
              value: YesNo.YES,
              label: 'Required'
            },
            {
              value: YesNo.NO,
              label: 'Not required'
            }
          ]}
          defaultValue={row.SOLUTIONS?.evd_required}
          onChange={(value: string) =>
            dispatch(
              solutionsOp.editSolutionValue(
                row.sol_id,
                row.SOLUTIONS,
                'evd_required',
                value
              )
            )
          }
        />
      )
    },

    {
      title: i18n.t('APPROVER'),
      width: 150,
      render: (_: any, row: ISolutionTableRow) => (
        <CustomSelect
          size='lg'
          options={Helpers.convertToSelectOptions(approversList)}
          defaultValue={row.SOLUTIONS?.approver}
          onChange={(value: string) =>
            dispatch(
              solutionsOp.editSolutionValue(
                row.sol_id,
                row.SOLUTIONS,
                'approver',
                value
              )
            )
          }
        />
      )
    },

    {
      title: 'Solution status',
      width: 150,
      render: (_: any, row: ISolutionTableRow) => {
        const isNewSolution = row.isNew === true;

        const isDisabled =
          isNewSolution || !permitted || !row.SOLUTIONS?.sol_approved;

        return (
          <CustomSelect
            size='lg'
            options={[
              {
                label: 'Approved',
                value: YesNo.YES
              },
              {
                label: 'Not approved',
                value: YesNo.NO
              }
            ]}
            defaultValue={isDisabled ? '' : row.SOLUTIONS?.sol_approved}
            disabled={isDisabled}
            onChange={(value: YesNo) => handleStatusChange(value, row.sol_id)}
          />
        );
      }
    }
  ];

  return solutionColumns;
};

const TaskColumns = () => {
  const taskColumns = [
    {
      title: i18n.t('SOLUTIONS'),
      width: 500,
      render: () => <TextBox width={500} />
    },
    {
      title: i18n.t('TASK_DEFINITION'),
      width: 366,
      render: () => <TextBox width={366} />
    },
    {
      title: i18n.t('TASK_TYPE'),
      width: 100,
      render: () => <TextBox width={100} />
    },
    {
      title: i18n.t('TASK_STATUS'),
      width: 120,
      render: () => <TextBox width={120} />
    },
    {
      title: i18n.t('OWNER'),
      width: 150,
      render: () => <TextBox width={150} />
    },
    {
      title: i18n.t('DUE_DATE'),
      width: 128,
      render: () => <TextBox width={128} />
    }
  ];
  return taskColumns;
};

const EvidenceDefinitionColumns = () => {
  const evidenceDefinitionColumns = [
    {
      title: i18n.t('CLN_REQ'),
      width: 500,
      render: () => <TextBox width={500} />
    },
    {
      title: i18n.t('SOLUTION_DEFINITION'),
      width: 500,
      render: () => <TextBox width={500} />
    },
    {
      title: i18n.t('SOLUTION_TYPE'),
      width: 100,
      render: () => <TextBox width={100} />
    },
    {
      title: i18n.t('EVIDENCE_DEFINITION'),
      width: 376,
      render: () => <TextBox width={376} />
    },
    {
      title: i18n.t('EVIDENCE_FILE'),
      width: 350,
      render: () => <TextBox width={350} />
    },
    {
      title: i18n.t('APPROVER'),
      width: 150,
      render: () => <TextBox width={150} />
    },
    {
      title: i18n.t('APPROVAL'),
      width: 150,
      render: () => <TextBox width={150} />
    }
  ];
  return evidenceDefinitionColumns;
};

const GapAdditionalInfoColumns = ({
  prioritization,
  gapSource,
  ownersList,
  handleSelectChange
}: {
  prioritization: string[];
  gapSource: string[];
  ownersList: string[];
  handleSelectChange: (
    value: string,
    type: string,
    data: IAdditionalInfo
  ) => void;
}) => [
  {
    title: i18n.t('GAPS'),
    dataIndex: 'gap_desc',
    width: 335,
    render: (value: string, row: any) => ({
      children: <TextBox width={335}>{stripHtmlTags(value)}</TextBox>,
      props: {
        rowSpan: row.gapRowSpan
      }
    })
  },
  {
    title: i18n.t('GAP_DUE_DATE'),
    dataIndex: 'gap_due_date',
    width: 300,
    render: (value: string, row: any) => ({
      children: (
        <CustomDatePicker
          defaultValue={value}
          onChange={(_: any, dateString: string) =>
            handleSelectChange(dateString, 'gap_due_date', row)
          }
        />
      ),
      props: {
        rowSpan: row.gapRowSpan
      }
    })
  },
  {
    title: i18n.t('DUE_DATE_AUDIT'),
    width: 195,
    render: (_: any, row: any) =>
      row.audit ? (
        <AuditsSelectBox audits={[row.audit]} valueKey='afg_due_date' dateEl />
      ) : (
        <TextBox width={195} />
      )
  },
  {
    title: i18n.t('PRIORITIZATION'),
    children: [
      {
        title: i18n.t('GAP'),
        dataIndex: 'gap_pri_ref',
        width: 150,
        render: (value: string, row: any) => (
          <CustomSelect
            size='md'
            options={Helpers.convertToSelectOptions(prioritization)}
            defaultValue={value}
            onChange={(v: string) => handleSelectChange(v, 'gap_pri_ref', row)}
          />
        )
      },
      {
        title: i18n.t('AUDIT_FINDING'),
        width: 180,
        render: (_: any, row: any) =>
          row.audit ? (
            <AuditsSelectBox
              audits={[row.audit]}
              valueKey='afg_prio_short_name'
            />
          ) : (
            <TextBox width={180} />
          )
      }
    ]
  },
  {
    title: i18n.t('GAP_SOURCE'),
    dataIndex: 'gap_src_ref',
    width: 120,
    render: (value: string, row: any) => (
      <CustomSelect
        size='md'
        options={Helpers.convertToSelectOptions(gapSource)}
        defaultValue={value}
        onChange={(v: string) => handleSelectChange(v, 'gap_src_ref', row)}
      />
    )
  },
  {
    title: i18n.t('SOURCE_AUDIT'),
    width: 195,
    render: (_: any, row: any) =>
      row.audit ? (
        <AuditsSelectBox audits={[row.audit]} valueKey='ads_short_name' />
      ) : (
        <TextBox width={195} />
      )
  },
  {
    title: i18n.t('OWNER_NOTIFIED'),
    dataIndex: 'gap_owner_notified',
    width: 100,
    render: (value: string, row: any) => (
      <StyledAdditional.StyledTag color={value === YesNo.YES ? 'green' : 'red'}>
        {value === YesNo.YES ? 'Yes' : 'No'}
      </StyledAdditional.StyledTag>
    )
  },
  {
    title: i18n.t('OWNER'),
    children: [
      {
        title: i18n.t('GAP'),
        dataIndex: 'gap_owner_ref',
        width: 150,
        render: (value: string, row: any) => (
          <CustomSelect
            size='lg'
            options={Helpers.convertToSelectOptions(ownersList)}
            defaultValue={value}
            onChange={(v: string) =>
              handleSelectChange(v, 'gap_owner_ref', row)
            }
          />
        )
      },
      {
        title: i18n.t('AUDIT'),
        width: 195,
        render: (_: any, row: any) =>
          row.audit ? (
            <AuditsSelectBox audits={[row.audit]} valueKey='afg_owner_ref' />
          ) : (
            <TextBox width={195} />
          )
      }
    ]
  }
];

export {
  SolutionColumns,
  TaskColumns,
  EvidenceDefinitionColumns,
  GapAdditionalInfoColumns,
  AuditFindingColumns
};
